import React from 'react';
import BaseModal from '../../utils/BaseModal/BaseModal';
import {
  AvatarFallback,
  CloseButton,
  CommentActionButton,
  CommentActionsRow,
  CommentAvatar,
  CommentBody,
  CommentCard,
  CommentComposer,
  CommentComposerButton,
  CommentComposerInput,
  CommentDeleteButton,
  CommentHeader,
  CommentLikeButton,
  CommentMeta,
  CommentName,
  CommentReplyText,
  CommentsBody,
  CommentsCountText,
  CommentsEmptyState,
  CommentsHeader,
  CommentsList,
  CommentsSheet,
  CommentsSubtitle,
  CommentText,
  CommentThread,
  ComposerInputRow,
  ComposerMetaRow,
  ComposerMetaText,
  InlineRepliesButton,
  LoadMoreButton,
  RepliesContainer,
} from './ReelCommentsModal.styles';
import { ReactComponent as CloseIcon } from '../../../assets/icons/close.svg';
import { ReactComponent as ChevronIcon } from '../../../assets/icons/chevron.svg';
import { ReactComponent as HeartIcon } from '../../../assets/icons/hearth.svg';
import { formatCompactCount, formatRelativeTime, truncateUsername } from '../../../utils/utils';

const ReelCommentsModal = ({
  isOpen,
  onClose,
  commentsCount,
  comments,
  isLoading,
  isSubmitting,
  composerPlaceholder,
  replyTargetName,
  commentText,
  onCommentTextChange,
  onCommentSubmit,
  onCommentLikeToggle,
  onReplyClick,
  onCancelReply,
  onDeleteComment,
  onLoadMoreComments,
  commentsHasMore,
  commentsFetchMorePending,
  onLoadReplies,
  onLoadMoreReplies,
}) => {
  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      desktopMode="dialog"
      desktopMaxWidth="62rem"
      customOverlayStyles={{ backgroundColor: 'rgba(3, 5, 9, 0.56)' }}
      customModalStyles={{ width: '100%' }}>
      <CommentsSheet>
        <CommentsHeader>
          <CommentsCountText>{formatCompactCount(commentsCount)} comments</CommentsCountText>
          <CloseButton type="button" onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        </CommentsHeader>
        <CommentsBody>
          {isLoading ? (
            <CommentsEmptyState>Loading comments...</CommentsEmptyState>
          ) : comments.length ? (
            <CommentsList>
              {comments.map(comment => (
                <CommentThread key={comment.id}>
                  <CommentItem
                    comment={comment}
                    onCommentLikeToggle={onCommentLikeToggle}
                    onReplyClick={onReplyClick}
                    onDeleteComment={onDeleteComment}
                  />
                  {comment.replies.length ? (
                    <RepliesContainer>
                      {comment.replies.map(reply => (
                        <CommentItem
                          key={reply.id}
                          comment={reply}
                          onCommentLikeToggle={onCommentLikeToggle}
                          onReplyClick={onReplyClick}
                          onDeleteComment={onDeleteComment}
                        />
                      ))}
                      {comment.repliesHasMore && comment.repliesNextCursor ? (
                        <InlineRepliesButton
                          type="button"
                          onClick={() => onLoadMoreReplies(comment.id)}
                          disabled={comment.repliesFetchMorePending}>
                          <ChevronIcon />
                          {comment.repliesFetchMorePending ? 'Loading replies...' : 'Load more replies'}
                        </InlineRepliesButton>
                      ) : null}
                    </RepliesContainer>
                  ) : null}
                  {comment.repliesCount > comment.replies.length && !comment.repliesLoaded ? (
                    <InlineRepliesButton
                      type="button"
                      onClick={() => onLoadReplies(comment.id)}
                      disabled={comment.repliesPending}>
                      <ChevronIcon />
                      {comment.repliesPending ? 'Loading replies...' : getViewRepliesLabel(comment.repliesCount)}
                    </InlineRepliesButton>
                  ) : null}
                </CommentThread>
              ))}
              {commentsHasMore ? (
                <LoadMoreButton type="button" onClick={onLoadMoreComments} disabled={commentsFetchMorePending}>
                  {commentsFetchMorePending ? 'Loading comments...' : 'Load more comments'}
                </LoadMoreButton>
              ) : null}
            </CommentsList>
          ) : (
            <CommentsEmptyState>No comments yet. Start the conversation.</CommentsEmptyState>
          )}
        </CommentsBody>
        <CommentComposer>
          {replyTargetName ? (
            <ComposerMetaRow>
              <ComposerMetaText>Replying to {replyTargetName}</ComposerMetaText>
              <CommentActionButton type="button" onClick={onCancelReply}>
                Cancel
              </CommentActionButton>
            </ComposerMetaRow>
          ) : null}
          <ComposerInputRow>
            <CommentComposerInput
              value={commentText}
              onChange={e => onCommentTextChange(e.target.value)}
              placeholder={composerPlaceholder}
              maxLength={1000}
            />
            <CommentComposerButton
              type="button"
              onClick={onCommentSubmit}
              disabled={!commentText.trim() || isSubmitting}>
              {isSubmitting ? 'Posting...' : 'Post'}
            </CommentComposerButton>
          </ComposerInputRow>
        </CommentComposer>
      </CommentsSheet>
    </BaseModal>
  );
};

const CommentItem = ({ comment, onCommentLikeToggle, onReplyClick, onDeleteComment }) => {
  return (
    <CommentCard>
      {comment.user?.avatar ? (
        <CommentAvatar src={comment.user.avatar} alt={comment.user.name} />
      ) : (
        <AvatarFallback>{getInitial(comment.user?.name || 'User')}</AvatarFallback>
      )}
      <CommentBody>
        <CommentHeader>
          <CommentMeta>
            <CommentName>{truncateUsername(comment.user?.name || 'User')}</CommentName>
            <CommentsSubtitle>{comment.createdAt ? formatRelativeTime(comment.createdAt) : ''}</CommentsSubtitle>
          </CommentMeta>
          <CommentLikeButton
            type="button"
            $active={comment.liked}
            onClick={() => onCommentLikeToggle(comment)}
            disabled={comment.likePending}>
            <HeartIcon />
            <span>{formatCompactCount(comment.likesCount)}</span>
          </CommentLikeButton>
        </CommentHeader>
        <CommentText>{comment.text}</CommentText>
        <CommentActionsRow>
          {!comment.parentCommentId && (
            <CommentActionButton type="button" onClick={() => onReplyClick(comment)}>
              Reply
            </CommentActionButton>
          )}
          {comment.repliesCount ? <CommentReplyText>{comment.repliesCount} replies</CommentReplyText> : null}
          {false ? (
            <CommentDeleteButton type="button" onClick={() => onDeleteComment(comment)}>
              Delete
            </CommentDeleteButton>
          ) : null}
        </CommentActionsRow>
      </CommentBody>
    </CommentCard>
  );
};

const getInitial = value => {
  return value?.trim()?.charAt(0)?.toUpperCase() || 'U';
};

const getViewRepliesLabel = repliesCount => {
  return `View ${repliesCount} ${repliesCount === 1 ? 'reply' : 'replies'}`;
};

export default ReelCommentsModal;
